import React from "react";
import { Link } from "react-router-dom";
import useSWR from "swr";
import { api } from "../api";
import { inline } from "../lib/markup";
import {
  ErrorNote,
  Footer,
  Loading,
  Nav,
  PageHead,
  Rule,
  ScatterBand,
  useBodyClass,
  useDocumentTitle,
} from "../components/Chrome";

const fetcher = (url) => api.get(url).then((r) => r.data);

function Section({ sec }) {
  return (
    <section data-testid={`essay-section-${sec.numeral}`}>
      <div className="prose">
        <p className="eyebrow sc">
          <span className="num">{sec.numeral}</span>
          <span>{sec.eyebrow}</span>
        </p>
        <h2>{sec.heading}</h2>

        {sec.verse ? (
          <p className="verse epigraph" data-testid={`essay-epigraph-${sec.numeral}`}>
            {sec.verse.text}
            <cite>{sec.verse.ref}</cite>
          </p>
        ) : null}

        {(sec.paras || []).map((p, i) => (
          <p key={i}>{inline(p)}</p>
        ))}

        {sec.pull ? (
          <blockquote className="q" data-testid={`essay-pull-${sec.numeral}`}>
            {inline(sec.pull)}
          </blockquote>
        ) : null}

        {sec.sig ? <p className="sig-line">{sec.sig}</p> : null}
      </div>
    </section>
  );
}

export default function Essay() {
  useBodyClass("inner");
  useDocumentTitle(
    "Why Sch\u0113ma?",
    "An essay on why Sch\u0113ma exists: the aesthetic reckoning, the return to sincerity, and the name.",
  );

  const { data, error, isLoading, mutate } = useSWR("/essay", fetcher);

  if (isLoading) {
    return (
      <>
        <Nav />
        <Loading label="Setting the type" testid="essay-loading" />
      </>
    );
  }

  if (error || !data) {
    return (
      <>
        <Nav />
        <section>
          <ErrorNote
            message="We couldn't load the essay just now."
            onRetry={() => mutate()}
            testid="essay-error"
          />
        </section>
        <Footer />
      </>
    );
  }

  const { meta, sections } = data;

  return (
    <>
      <Nav />
      <PageHead
        eyebrow={meta.eyebrow}
        title={meta.title}
        standfirst={meta.standfirst}
        testid="essay-page-head"
      />

      <div className="long-read essay-read">
        {(sections || []).map((sec, i) => (
          <React.Fragment key={sec.numeral}>
            <Section sec={sec} />
            {i < sections.length - 1 ? (
              i % 2 === 0 ? (
                <Rule />
              ) : (
                <ScatterBand />
              )
            ) : null}
          </React.Fragment>
        ))}
      </div>

      <Rule />

      {/* Back to the document this essay sits beside. */}
      <section data-testid="essay-to-rule">
        <div className="prose">
          <div className="panel">
            <p className="eyebrow sc">
              <span className="num">&middot;</span>
              <span>Alongside this</span>
            </p>
            <h3>The Rule</h3>
            <p>
              The essay is the why. The Rule is the shape it takes: what we mean
              by Christian art, what we actually do, and the habits we hold each
              other to.
            </p>
            <div className="actions">
              <Link to="/rule" className="act" data-testid="essay-rule-link">
                Read the Rule &rarr;
              </Link>
              <Link to="/join" className="linkish" data-testid="essay-join-link">
                Or come to something
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
